import {
  Box,
  BoxProps,
  Button,
  Divider,
  useMediaQuery,
} from "@chakra-ui/react";
import { Steps } from "chakra-ui-steps";
import {
  BookCard,
  BookCardTitle,
  BookCardSubtitle,
} from "../components/Book/BookCard";
import { BaseStep } from "../components/Base/BaseStep";
import { useBookingContext } from "../context/BookingContext";
import { useLocation } from "react-router-dom";
import { useMemo } from "react";
import { BaseText, Font } from "../components/Base/BaseText";
import { useHistory } from "react-router-dom";

type PriceLineProps = BoxProps & {
  label: string;
  color?: string;
  price?: number | string;
  labelFont?: Font;
  priceFont?: Font;
};
export const PriceLine: React.FC<PriceLineProps> = ({
  color,
  label,
  price,
  labelFont = ["Inter", 400, "16px", "24px"],
  priceFont = ["Inter", 400, "16px", "24px"],
  ...props
}) => {
  return (
    <Box
      {...props}
      color={color}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
    >
      <BaseText font={labelFont}>{label}</BaseText>
      {price && <BaseText font={priceFont}>{price}</BaseText>}
    </Box>
  );
};

type BookSummaryProps = BoxProps & {
  onBook: () => void;
};
const BookSummary: React.FC<BookSummaryProps> = ({ onBook, ...props }) => {
  return (
    <BookCard {...props}>
      <BaseText color="#202020" font={["Cairo", 700, "18px", "35px"]}>
        Order Summary
      </BaseText>
      <PriceLine price="$250" color="#27489B" label="Robotics: Beginner" />
      <PriceLine color="#6B7280" label="4 Sessions per month" />
      <PriceLine price="$0.00" color="#6B7280" label="Plus tax" />
      <PriceLine price="$0.00" color="#6B7280" label="Sub-total" />
      <Divider borderColor="#E5E7EB" my="4" />
      <PriceLine
        py="3"
        px="4"
        bg="#EFF6FF"
        price="$0.00"
        color="#27489B"
        borderRadius="9px"
        label="Total due today"
        labelFont={["Inter", 500, "18px", "28px"]}
        priceFont={["Inter", 700, "18px", "28px"]}
      />
      <Divider borderColor="#E5E7EB" my="4" />
      <BaseText
        color="#000000"
        textDecoration="underline"
        font={["Inter", 500, "sm", "24px"]}
      >
        I have a coupon or referral code
      </BaseText>
      <Button
        mt="4"
        h={"44px"}
        width={"full"}
        rounded="12px"
        fontSize={"sm"}
        border={"none"}
        display={"block"}
        fontFamily="Cairo"
        colorScheme={"blue"}
        bg="#23428D"
        _hover={{ bg: "#1D2B7E" }}
        boxShadow={"0px 1px 2px rgba(31, 41, 55, 0.08)"}
        onClick={onBook}
      >
        Book Now
      </Button>
      <BaseText mt="4" color="#6B7280" font={["Inter", 400, "sm", "24px"]}>
        By placing your order, you agree to our company{" "}
        <a href="#privacy-policy" style={{ textDecoration: "underline" }}>
          Privacy Policy
        </a>{" "}
        and{" "}
        <a href="#privacy-policy" style={{ textDecoration: "underline" }}>
          Conditions of Use.
        </a>
      </BaseText>
    </BookCard>
  );
};

const WhatHappensNext: React.FC = () => {
  return (
    <BookCard mt={"10px"}>
      <BaseText color="#202020" font={["Cairo", 700, "18px", "35px"]}>
        What happens next?
      </BaseText>

      {/* Set up your session */}
      <BaseText
        mt="4"
        color={"#27489B"}
        display="flex"
        alignItems="center"
        font={["Cairo", 700, "16px", "25px"]}
        experimental_spaceX={"6.82px"}
      >
        <i className="ri-calendar-2-line" style={{ fontSize: "18px" }}></i>
        <span>Set up your session</span>
      </BaseText>
      <BaseText mt="2" color="#374151" font={["Inter", 400, "sm", "20px"]}>
        We'll help you choose your child's course and session time. If anything
        changes later, you can always adjust your schedule, courses, or plan.
      </BaseText>

      {/* Get personalized instructor matching */}
      <BaseText
        mt="4"
        color={"#27489B"}
        display="flex"
        alignItems="center"
        font={["Cairo", 700, "16px", "25px"]}
        experimental_spaceX={"6.82px"}
      >
        <i className="ri-body-scan-fill" style={{ fontSize: "18px" }}></i>
        <span>Get personalized instructor matching</span>
      </BaseText>
      <BaseText mt="2" color="#374151" font={["Inter", 400, "sm", "20px"]}>
        Our team will find instructors who are right for your child's learning
        style and preferences. We'll match you with one instructor per each of
        your subjects.
      </BaseText>

      {/* Start learning! */}
      <BaseText
        mt="4"
        color={"#27489B"}
        display="flex"
        alignItems="center"
        font={["Cairo", 700, "16px", "25px"]}
        experimental_spaceX={"6.82px"}
      >
        <i className="ri-file-list-2-line" style={{ fontSize: "18px" }}></i>
        <span>Start learning!</span>
      </BaseText>
      <BaseText mt="2" color="#374151" font={["Inter", 400, "sm", "20px"]}>
        Get free session to see if our program is right for your child. If
        you're not satisfied, cancel your plan at no cost.
      </BaseText>
    </BookCard>
  );
};

const BookLayout: React.FC = ({ children }) => {
  const { stepr } = useBookingContext();

  const history = useHistory();
  const location = useLocation();
  const [isSmall] = useMediaQuery("(max-width: 992px)");

  const isConfirmPage = useMemo(
    () => location.pathname.includes("/confirm"),
    [location.pathname]
  );

  const steps = useMemo(
    () => [
      { label: `Guardian${!isSmall ? " info" : ""}` },
      { label: "Child info" },
      { label: "Class" },
      { label: "Schedule" },
      { label: "Confirm" },
    ],
    [isSmall]
  );

  const handleClickBookNow = () => {
    /**
     * TODO: validate form
     */
    // stepr.nextStep();
    history.push("/book/success");
  };

  return (
    <Box bg={"#F9F9F9"}>
      <Box height={"100vh"} px={{ base: 4, lg: 0 }}>
        <Box
          mx="auto"
          display="flex"
          flexDirection={{ base: "column-reverse", lg: "row" }}
          w={{ lg: "max-content" }}
          experimental_spaceX={2}
        >
          <Box flexShrink={0} flexGrow={1} w={{ base: "full", lg: "800px" }}>
            <BookCard
              mt={{ base: "16px", lg: "60px" }}
              mx={isConfirmPage ? "0" : "auto"}
            >
              <BookCardTitle>
                {isConfirmPage ? "Get Started" : "Book Your Free Trial Class"}
              </BookCardTitle>
              <BookCardSubtitle mt={{ base: "4px", lg: "10px" }}>
                Get Started with Your MakerKids Account
              </BookCardSubtitle>

              <Box
                pb="4"
                mt={4}
                pt={{ base: 0, lg: 4 }}
                px={{ base: 0, lg: 4 }}
              >
                <Steps
                  pt={"6px"}
                  pb={"6px"}
                  flexDirection="row"
                  activeStep={stepr.activeStep}
                >
                  {steps.map((step, stepIndex) => (
                    <BaseStep key={stepIndex} label={step.label} />
                  ))}
                </Steps>
              </Box>
            </BookCard>

            {isConfirmPage && isSmall && (
              <>
                <BookSummary mt={"10px"} onBook={handleClickBookNow} />
                <WhatHappensNext />
              </>
            )}

            {children}
          </Box>

          {/* Summary */}
          {isConfirmPage && !isSmall && (
            <Box maxW="376px" mt={"60px"} mb={"60px"} flexShrink={0}>
              <BookSummary onBook={handleClickBookNow} />
              <WhatHappensNext />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export { BookLayout };
